import { Injectable } from '@angular/core';
import { db } from './db.service';
import * as _moment from 'moment';
const moment = _moment;

export interface IMaterialSnapshot {
  id?: number;
  materialId: number;
  stockGroupId: number;
  yearMonth: string; // "YYYY-MM" format
  openingBalance: number;
  closingBalance: number;
}

export interface IMaterialDelta {
  id?: number;
  materialId: number;
  stockGroupId: number;
  date: string; // "YYYY-MM-DD" format
  quantityChange: number; // Positive for additions, negative for usage
  sourceType: 'production' | 'stock-entry' | 'stock-out' | 'sell' | 'return' | 'transfer' | 'other';
  sourceId: string | number; // Transaction ID or temp ID
  isProduction: boolean;
}

@Injectable({
  providedIn: 'root'
})
export class SnapshotService {
  
  constructor() {}

  /**
   * Get material balance as of a specific date
   * This is the FAST query that replaces scanning 50,000+ stock records
   * 
   * @param materialId - Material to check
   * @param stockGroupId - Stock group to check
   * @param asOfDate - Date in "YYYY-MM-DD" format
   * @returns Current balance
   */
  async getMaterialBalance(
    materialId: number, 
    stockGroupId: number, 
    asOfDate: string
  ): Promise<number> {
    const yearMonth = asOfDate.substring(0, 7); // "2024-11"
    
    // 1. Get the monthly snapshot (opening balance)
    const snapshot = await db.materialSnapshots
      .where('[materialId+stockGroupId+yearMonth]')
      .equals([materialId, stockGroupId, yearMonth])
      .first();
    
    let balance = snapshot?.openingBalance || 0;
    
    // 2. Add all deltas from month start to the target date
    const monthStart = yearMonth + '-01';
    const deltas = await db.materialDeltas
      .where('[materialId+stockGroupId+date]')
      .between(
        [materialId, stockGroupId, monthStart], 
        [materialId, stockGroupId, asOfDate],
        true, // Include lower bound
        true  // Include upper bound
      )
      .toArray();
    
    // Sum up all changes
    const deltaSum = deltas.reduce((sum, d) => sum + d.quantityChange, 0);
    
    return balance + deltaSum;
  }

  /**
   * Get balance for multiple materials at once (batch operation)
   */
  async getMaterialBalances(
    materials: Array<{ materialId: number; stockGroupId: number }>,
    asOfDate: string
  ): Promise<Map<string, number>> {
    const results = new Map<string, number>();
    
    // Process in parallel for better performance
    await Promise.all(
      materials.map(async ({ materialId, stockGroupId }) => {
        const balance = await this.getMaterialBalance(materialId, stockGroupId, asOfDate);
        const key = `${materialId}_${stockGroupId}`;
        results.set(key, balance);
      })
    );
    
    return results;
  }

  /**
   * Add a delta (change) to the system
   * Call this whenever stock changes (production, purchase, sale, transfer)
   * 
   * @param delta - The delta to add
   */
  async addDelta(delta: IMaterialDelta): Promise<void> {
    await db.materialDeltas.add(delta);
  }

  /**
   * Add multiple deltas at once (for transactions affecting multiple materials)
   */
  async addDeltas(deltas: IMaterialDelta[]): Promise<void> {
    if (deltas.length === 0) return;
    await db.materialDeltas.bulkAdd(deltas);
  }

  /**
   * Rebuild a month's snapshot from scratch
   * Used when historical edits are made
   * 
   * @param materialId - Material to rebuild
   * @param stockGroupId - Stock group to rebuild
   * @param yearMonth - Month in "YYYY-MM" format
   */
  async rebuildMonthSnapshot(
    materialId: number,
    stockGroupId: number,
    yearMonth: string
  ): Promise<void> {
    const monthStart = yearMonth + '-01';
    const monthEnd = moment(monthStart).endOf('month').format('YYYY-MM-DD');
    
    // 1. Get opening balance (closing balance of previous month)
    const prevMonth = moment(monthStart).subtract(1, 'month').format('YYYY-MM');
    const prevSnapshot = await db.materialSnapshots
      .where('[materialId+stockGroupId+yearMonth]')
      .equals([materialId, stockGroupId, prevMonth])
      .first();
    
    const openingBalance = prevSnapshot?.closingBalance || 0;
    
    // 2. Sum all deltas in this month
    const deltas = await db.materialDeltas
      .where('[materialId+stockGroupId+date]')
      .between(
        [materialId, stockGroupId, monthStart],
        [materialId, stockGroupId, monthEnd],
        true,
        true
      )
      .toArray();
    
    const deltaSum = deltas.reduce((sum, d) => sum + d.quantityChange, 0);
    const closingBalance = openingBalance + deltaSum;
    
    // 3. Update or create snapshot
    const existing = await db.materialSnapshots
      .where('[materialId+stockGroupId+yearMonth]')
      .equals([materialId, stockGroupId, yearMonth])
      .first();
    
    if (existing) {
      await db.materialSnapshots.update(existing.id!, {
        openingBalance,
        closingBalance
      });
    } else {
      await db.materialSnapshots.add({
        materialId,
        stockGroupId,
        yearMonth,
        openingBalance,
        closingBalance
      });
    }
  }

  /**
   * Handle a historical edit by rebuilding affected months
   * Only rebuilds from the edited month forward to current month
   * 
   * @param editedDate - Date of the historical edit in "YYYY-MM-DD"
   * @param affectedMaterials - Materials that were affected by the edit
   */
  async handleHistoricalEdit(
    editedDate: string,
    affectedMaterials: Array<{ materialId: number; stockGroupId: number }>
  ): Promise<void> {
    const editedMonth = editedDate.substring(0, 7);
    const currentMonth = moment().format('YYYY-MM');
    
    // Get all months between edited month and current month
    const monthsToRebuild = this.getMonthsBetween(editedMonth, currentMonth);
    
    // Rebuild each affected material for each month
    for (const { materialId, stockGroupId } of affectedMaterials) {
      for (const month of monthsToRebuild) {
        await this.rebuildMonthSnapshot(materialId, stockGroupId, month);
      }
    }
  }

  /**
   * Helper: Get array of months between two dates (inclusive)
   */
  private getMonthsBetween(startMonth: string, endMonth: string): string[] {
    const months: string[] = [];
    let current = moment(startMonth + '-01');
    const end = moment(endMonth + '-01');
    
    while (current.isSameOrBefore(end)) {
      months.push(current.format('YYYY-MM'));
      current.add(1, 'month');
    }
    
    return months;
  }

  /**
   * Initialize snapshots for a new material/stockGroup combination
   * Call this when creating the first delta for a material
   */
  async initializeSnapshot(
    materialId: number,
    stockGroupId: number,
    yearMonth: string,
    openingBalance: number = 0
  ): Promise<void> {
    const existing = await db.materialSnapshots
      .where('[materialId+stockGroupId+yearMonth]')
      .equals([materialId, stockGroupId, yearMonth])
      .first();
    
    if (!existing) {
      await db.materialSnapshots.add({
        materialId,
        stockGroupId,
        yearMonth,
        openingBalance,
        closingBalance: openingBalance
      });
    }
  }

  /**
   * Get all deltas for a specific transaction (for debugging/audit trail)
   */
  async getDeltasForTransaction(sourceId: string | number): Promise<IMaterialDelta[]> {
    return await db.materialDeltas
      .where('sourceId')
      .equals(sourceId)
      .toArray();
  }

  /**
   * Delete deltas for a transaction (when a transaction is removed/edited)
   */
  async deleteDeltasForTransaction(sourceId: string | number): Promise<void> {
    const deltas = await this.getDeltasForTransaction(sourceId);
    const deltaIds = deltas.map(d => d.id!);
    
    if (deltaIds.length > 0) {
      await db.materialDeltas.bulkDelete(deltaIds);
      
      // Rebuild affected months
      const affectedMaterials = deltas.map(d => ({
        materialId: d.materialId,
        stockGroupId: d.stockGroupId
      }));
      
      // Get unique combinations
      const uniqueMaterials = Array.from(
        new Map(affectedMaterials.map(m => [`${m.materialId}_${m.stockGroupId}`, m])).values()
      );
      
      if (deltas.length > 0 && deltas[0].date) {
        await this.handleHistoricalEdit(deltas[0].date, uniqueMaterials);
      }
    }
  }

  /**
   * MIGRATION UTILITY: Convert existing stock entries to snapshot system
   * Run this ONCE to migrate historical data
   * Processes one month at a time to avoid blocking
   */
  async migrateToSnapshotSystem(
    startDate: string = '2020-01-01',
    endDate?: string
  ): Promise<{ monthsProcessed: number; deltasCreated: number }> {
    const end = endDate || moment().format('YYYY-MM-DD');
    const months = this.getMonthsBetween(
      startDate.substring(0, 7),
      end.substring(0, 7)
    );
    
    let totalDeltas = 0;
    
    for (const month of months) {
      const monthStart = month + '-01';
      const monthEnd = moment(monthStart).endOf('month').format('YYYY-MM-DD');
      
      // Get all stock entries for this month
      const stockEntries = await db.stock
        .where('date')
        .between(monthStart, monthEnd, true, true)
        .toArray();
      
      // Convert to deltas
      const deltas: IMaterialDelta[] = stockEntries.map(entry => ({
        materialId: entry.materialId,
        stockGroupId: entry.stockGroupId,
        date: entry.date,
        quantityChange: entry.quantity,
        sourceType: this.determineSourceType(entry),
        sourceId: entry.transactionId || entry.tId,
        isProduction: entry.quantity < 0 // Negative quantities are typically production usage
      }));
      
      if (deltas.length > 0) {
        await this.addDeltas(deltas);
        totalDeltas += deltas.length;
      }
      
      // Get unique material/stockGroup combinations
      const combinations = new Set<string>();
      stockEntries.forEach(entry => {
        combinations.add(`${entry.materialId}_${entry.stockGroupId}`);
      });
      
      // Rebuild snapshots for each combination
      for (const combo of combinations) {
        const [materialId, stockGroupId] = combo.split('_').map(Number);
        await this.rebuildMonthSnapshot(materialId, stockGroupId, month);
      }
      
      console.log(`Migrated month ${month}: ${deltas.length} deltas created`);
    }
    
    return {
      monthsProcessed: months.length,
      deltasCreated: totalDeltas
    };
  }

  /**
   * Helper to determine source type from stock entry
   */
  private determineSourceType(stockEntry: any): IMaterialDelta['sourceType'] {
    // You can enhance this based on your transaction types
    if (stockEntry.quantity < 0) {
      return 'production'; // Negative is typically production usage
    } else {
      return 'stock-entry'; // Positive is typically purchase
    }
  }
}
